// Sliders API client (MOD-10, config-tier) — slider + görsel alt kaynağı.
//
// Uçlar `/api/v1/sliders/...` altında, `web.manage` policy'siyle korunur.
// Publish iki-durum toggle (POST /{id}/publish?publish=bool). Görsel create/
// update/reorder BooleanActionResponse döner → çağıran slider'ı yeniden çeker.
// İş kuralları BE'de: INVALID_PLACEMENT / LANGUAGE_INACTIVE / REORDER_DUPLICATE_*.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import {
  normalizeConfigStatus,
  type CreateSliderImageRequest,
  type CreateSliderRequest,
  type Slider,
  type SliderCreateInput,
  type SliderDtoWire,
  type SliderImage,
  type SliderImageDtoWire,
  type SliderImageInput,
  type SliderImageOrderWire,
  type SliderListItem,
  type SliderListItemDtoWire,
  type SliderUpdateInput,
  type UpdateSliderImageRequest,
  type UpdateSliderRequest,
} from '@features/widgets/types';

const BASE = '/api/v1/sliders';

interface SliderListQuery {
  placement?: string;
  languageId?: string;
}

function mapListItem(d: SliderListItemDtoWire): SliderListItem {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    placement: d.placement ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    imageCount: d.imageCount ?? 0,
    updatedAt: d.updatedAt ?? '',
  };
}

function mapImage(d: SliderImageDtoWire): SliderImage {
  return {
    id: d.id ?? '',
    sliderId: d.sliderId ?? '',
    imageUrl: d.imageUrl ?? '',
    title: d.title ?? null,
    subtitle: d.subtitle ?? null,
    ctaText: d.ctaText ?? null,
    ctaLink: d.ctaLink ?? null,
    order: d.order ?? 0,
    startDate: d.startDate ?? null,
    endDate: d.endDate ?? null,
    isActive: d.isActive ?? false,
  };
}

function mapSlider(d: SliderDtoWire): Slider {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    placement: d.placement ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    variantLanguageCodes: d.variantLanguageCodes ?? [],
    images: (d.images ?? []).map(mapImage).sort((a, b) => a.order - b.order),
  };
}

export const slidersApi = {
  async list(query: SliderListQuery = {}): Promise<SliderListItem[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 200 };
    if (query.placement) params.placement = query.placement;
    if (query.languageId) params.languageId = query.languageId;
    const res = await http.get<ActionResponse<SliderListItemDtoWire>>(BASE, { params });
    return unwrapList(res).items.map(mapListItem);
  },

  async get(id: string): Promise<Slider> {
    return mapSlider(unwrap(await http.get<ActionResponse<SliderDtoWire>>(`${BASE}/${id}`)));
  },

  async create(input: SliderCreateInput): Promise<Slider> {
    const body: CreateSliderRequest = {
      name: input.name,
      placement: input.placement,
      ...(input.languageId ? { languageId: input.languageId } : {}),
    };
    return mapSlider(unwrap(await http.post<ActionResponse<SliderDtoWire>>(BASE, body)));
  },

  async update(id: string, input: SliderUpdateInput): Promise<Slider> {
    const body: UpdateSliderRequest = { name: input.name, placement: input.placement };
    unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body));
    return slidersApi.get(id);
  },

  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },

  async setPublished(id: string, publish: boolean): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(`${BASE}/${id}/publish`, null, {
        params: { publish },
      }),
    );
  },

  images: {
    async create(sliderId: string, input: SliderImageInput): Promise<void> {
      const body: CreateSliderImageRequest = {
        imageUrl: input.imageUrl,
        title: input.title ?? null,
        subtitle: input.subtitle ?? null,
        ctaText: input.ctaText ?? null,
        ctaLink: input.ctaLink ?? null,
        order: input.order ?? null,
        startDate: input.startDate ?? null,
        endDate: input.endDate ?? null,
        isActive: input.isActive,
      };
      unwrap(await http.post<ActionResponse<boolean>>(`${BASE}/${sliderId}/images`, body));
    },

    async update(imageId: string, input: SliderImageInput): Promise<void> {
      const body: UpdateSliderImageRequest = {
        imageUrl: input.imageUrl,
        title: input.title ?? null,
        subtitle: input.subtitle ?? null,
        ctaText: input.ctaText ?? null,
        ctaLink: input.ctaLink ?? null,
        startDate: input.startDate ?? null,
        endDate: input.endDate ?? null,
        isActive: input.isActive,
      };
      unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/images/${imageId}`, body));
    },

    async remove(imageId: string): Promise<void> {
      unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/images/${imageId}`));
    },

    async reorder(sliderId: string, ordered: { id: string; order: number }[]): Promise<void> {
      const items: SliderImageOrderWire[] = ordered.map((o) => ({ id: o.id, order: o.order }));
      unwrap(
        await http.put<ActionResponse<boolean>>(`${BASE}/${sliderId}/images/reorder`, { items }),
      );
    },
  },
};
